import type { MealKey } from "@/data/recipes";

type PendingDietPreferences = Record<MealKey, string[]>;

const SESSION_KEY = "pendingDietPreferences";
const LOCAL_KEY = "pendingDietPreferencesBackup";
const TTL_MS = 24 * 60 * 60 * 1000;

interface PendingDietPayload {
  preferences: PendingDietPreferences;
  savedAt: number;
}

function isValidPreferences(value: unknown): value is PendingDietPreferences {
  if (!value || typeof value !== "object") return false;

  const keys: MealKey[] = ["breakfast", "morning_snack", "lunch", "snack", "dinner", "optional"];
  return keys.every((key) => Array.isArray((value as Record<string, unknown>)[key]));
}

function parsePayload(raw: string | null): PendingDietPayload | null {
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);

    if (isValidPreferences(parsed)) {
      return { preferences: parsed, savedAt: Date.now() };
    }

    if (
      parsed &&
      typeof parsed === "object" &&
      typeof parsed.savedAt === "number" &&
      isValidPreferences(parsed.preferences)
    ) {
      if (Date.now() - parsed.savedAt > TTL_MS) return null;
      return parsed as PendingDietPayload;
    }
  } catch {
    return null;
  }

  return null;
}

export function savePendingDietPreferences(preferences: PendingDietPreferences) {
  const payload: PendingDietPayload = {
    preferences,
    savedAt: Date.now(),
  };

  const rawPreferences = JSON.stringify(preferences);
  const rawPayload = JSON.stringify(payload);

  sessionStorage.setItem(SESSION_KEY, rawPreferences);
  localStorage.setItem(LOCAL_KEY, rawPayload);
}

export function loadPendingDietPreferences(): PendingDietPreferences | null {
  const sessionPayload = parsePayload(sessionStorage.getItem(SESSION_KEY));
  if (sessionPayload) {
    localStorage.setItem(LOCAL_KEY, JSON.stringify(sessionPayload));
    return sessionPayload.preferences;
  }

  const localPayload = parsePayload(localStorage.getItem(LOCAL_KEY));
  if (localPayload) {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(localPayload.preferences));
    return localPayload.preferences;
  }

  clearPendingDietPreferences();
  return null;
}

export function hasPendingDietPreferences() {
  return !!loadPendingDietPreferences();
}

export function clearPendingDietPreferences() {
  sessionStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(LOCAL_KEY);
}
